// Round-complete celebration: a headline sampled from the tier you landed in,
// and a burst of one randomly chosen emoji.
//
// Sampled rather than fixed so consecutive rounds don't read identically - the
// same argument as giving the labs their own look: variety is what keeps a
// short loop from going stale.

// Honest per tier. The bottom tier has no exclamation mark on purpose: "Keep
// at it!" after 6/20 reads as sarcasm.
const HEADLINES = [
  { min: 100, options: ["Perfect round!", "Flawless!", "Every single one!", "Nailed it!"] },
  { min: 90, options: ["Great round!", "Strong work!", "So close to perfect!"] },
  { min: 75, options: ["Nice work!", "Good round!", "That's solid!"] },
  { min: 50, options: ["Good progress!", "Getting there!", "Coming along!"] },
  { min: 0, options: ["This is the hard part.", "These ones are tricky.", "Keep at it."] },
];

const BURST_EMOJI = ["🎉", "✨", "🥳", "🌟", "🎊", "🙌", "💪", "🔥", "👏", "⭐️"];

// Every finished round gets a burst. Gating it on accuracy meant a beginner -
// who will sit at 40-60% for weeks, and who most needs the encouragement -
// never saw one. The headline is the honest feedback; the burst is the reward
// for finishing. Size carries the signal instead of presence.
const BURST_TIERS = [
  { max: [40, 26], min: 90, pieces: 40 },
  { max: [22, 36], min: 75, pieces: 30 },
  { max: [18, 30], min: 50, pieces: 22 },
  { max: [16, 26], min: 0, pieces: 14 },
];

export function burstTier(accuracy) {
  return BURST_TIERS.find((tier) => accuracy >= tier.min);
}

export function burstCount(accuracy) {
  return burstTier(accuracy).pieces;
}

const sample = (options, random) => options[Math.floor(random() * options.length)];

const between = (min, max, random) => min + random() * (max - min);

export function pickHeadline(accuracy, answered, random = Math.random) {
  if (answered === 0) return "Round done.";

  return sample(HEADLINES.find((tier) => accuracy >= tier.min).options, random);
}

export function pickEmoji(random = Math.random) {
  return sample(BURST_EMOJI, random);
}

export function shouldBurst(accuracy, answered) {
  return answered > 0;
}

// Each piece gets its own force, so the burst has depth instead of reading as
// one rigid shape: how high it goes, how far it drifts, how far it falls, how
// fast it spins, and how big it is.
export function burstPieces(accuracy, random = Math.random) {
  const tier = burstTier(accuracy);
  const [smallest, largest] = tier.max;

  return Array.from({ length: tier.pieces }, (_, i) => ({
    // Tight, so it reads as one explosion rather than a trickle.
    delay: Math.round(between(0, 140, random)),
    drift: Math.round(between(-380, 380, random)),
    duration: Math.round(between(1500, 2400, random)),
    fall: Math.round(between(140, 300, random)),
    key: i,
    rise: Math.round(between(90, 240, random)),
    size: Math.round(between(smallest, largest, random)),
    spin: Math.round(between(-640, 640, random)),
  }));
}
